import React from 'react';
import PropTypes from 'prop-types';
import clone from 'clone';

import {withIsOpenState} from '../../../lib/react/higher-order-components';
import {oeInterfaceManager} from '../../../react-oe/oe-interface';
import OEInterfaceAdapter from '../../../react-oe/oe-interface-adapter';
import CustomScrollbar, {CustomScrollbarTrackVisibility} from '../../../lib/custom-scrollbar/custom-scrollbar';
import OEIconCodes from '../../../lib/oe-icon-codes';
import OEToolbox from '../../../lib/tools/oe-toolbox';
import retardUpdate from '../../../lib/react/update-retarder';
import OEIcon from '../../elements/oe-icon';
import OEButton from '../../elements/controls/oe-button';
import OEPopover from '../../views/oe-popover';
import {OEAssetSelectionType} from './oe-asset-selection-model';
import {OEAssetSelectionCell} from './oe-asset-selection-cells';

export class OEAssetSelectionController extends React.PureComponent {

    constructor(props)  {
        super(props);

        this.oe = oeInterfaceManager.getInterface(this.props.moduleId);

        this.animations = [];
        this.presentations = [];
        this.mediaCenterItems = [];
        this.selection = [];

        this.state = {
            animations: clone(this.animations),
            presentations: clone(this.presentations),
            mediaCenterItems: clone(this.mediaCenterItems),
            hasSelection: false
        };

        this.onPresentationAdded = this.onPresentationAdded.bind(this);
        this.onPresentationRemoved = this.onPresentationRemoved.bind(this);

        this.onMediaCenterItemAdded = this.onMediaCenterItemAdded.bind(this);
        this.onMediaCenterItemRemoved = this.onMediaCenterItemRemoved.bind(this);
        this.onMediaCenterItemDataChanged = this.onMediaCenterItemDataChanged.bind(this);

        this.onCellClick = this.onCellClick.bind(this);
        this.onOkBtnPressed = this.onOkBtnPressed.bind(this);
    }

    hasType(type, props) {
        if(!type)   return false;

        if(Array.isArray(type)) {
            for(let i = 0; i < type.length; ++i)    {
                if(this.hasType(type[i], props))    return true;
            }
            return false;
        }
        props = props || this.props;
        return props.types.includes(type);
    }

    hasAnimationType(props) {
        return this.hasType(OEAssetSelectionType.animation, props);
    }

    hasPresentationType(props) {
        return this.hasType(OEAssetSelectionType.presentation, props);
    }

    hasMediaCenterType(props) {
        return this.hasType(OEAssetSelectionType.mediaCenter, props);
    }

    componentWillReceiveProps(nextProps) {

        if(this.hasAnimationType(nextProps) != this.hasAnimationType()) {
            this.updateAnimations(nextProps);
        }

        if(this.hasPresentationType(nextProps) != this.hasPresentationType()) {
            this.updatePresentations(nextProps);
        }

        if(this.hasMediaCenterType(nextProps) != this.hasMediaCenterType()) {
            this.updateMediaCenterItems(nextProps);
        }

        if(nextProps.allowsMultipleSelection !== this.props.allowsMultipleSelection && !nextProps.allowsMultipleSelection && this.selection.length > 1)  {
            this.setSelection(this.selection[0], nextProps);
        }
    }

    onConnect()  {
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.presentationAdded, this.onPresentationAdded);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.presentationRemoved, this.onPresentationRemoved);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.mediaCenterItemAdded, this.onMediaCenterItemAdded);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.mediaCenterItemRemoved, this.onMediaCenterItemRemoved);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.mediaCenterItemDataChanged, this.onMediaCenterItemDataChanged);
    }

    onRelease()    {
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.presentationAdded, this.onPresentationAdded);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.presentationRemoved, this.onPresentationRemoved);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.mediaCenterItemAdded, this.onMediaCenterItemAdded);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.mediaCenterItemRemoved, this.onMediaCenterItemRemoved);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.mediaCenterItemDataChanged, this.onMediaCenterItemDataChanged);
    }

    getIndex(item)  {
        if(!item)   return -1;

        let indexOffset = 0;

        if(item.type === OEAssetSelectionType.animation)    {
            let index = this.animations.findIndex(item_ => item_.ids.first === item.ids.first && item_.ids.second === item.ids.second);
            if(index >= 0)  return index >= 0 ? indexOffset + index : -1;
        }

        indexOffset += this.animations.length;

        if(item.type === OEAssetSelectionType.presentation)    {
            let index = this.presentations.findIndex(item_ => item_.ids.first === item.ids.first && item_.ids.second === item.ids.second);
            return index >= 0 ? indexOffset + index : -1;
        }
        
        indexOffset += this.presentations.length;

        if(OEAssetSelectionType.mediaCenter.includes(item.type))    {
            let index = this.mediaCenterItems.findIndex(item_ => item_.ids.first === item.ids.first && item_.ids.second === item.ids.second);
            return index >= 0 ? indexOffset + index : -1;
        }

        return -1;
    }

    getItem(index)  {
        if(index < 0)   return undefined;

        if(index < this.animations.length)  return {type: OEAssetSelectionType.animation, ids: clone(this.animations[index].ids)};
        index -= this.animations.length;

        if(index < this.presentations.length)  return {type: OEAssetSelectionType.presentation, ids: clone(this.presentations[index].ids)};
        index -= this.presentations.length;

        if(index < this.mediaCenterItems.length)  return {type: this.mediaCenterItems[index].type, ids: clone(this.mediaCenterItems[index].ids)};
    }

    getSortedSelection()    {
        let selection = this.selection.map(item => {
            return {
                type: item.type,
                ids: clone(item.ids),
                index: this.getIndex(item)
            };
        });

        selection = selection.sort((l, r) => l.index - r.index);

        // filter out duplicates & items with invalid index -> this should not be necessary but we do it nevertheless for the sake of a forgiving coding paradigm
        selection = selection.filter((item, i) => item.index >= 0 && (!i || item.index != selection[i - 1].index));

        return selection;
    }

    getItemListForType(type)    {
        if(type === OEAssetSelectionType.animation) {
            return this.animations;
        } else if(type === OEAssetSelectionType.presentation) {
            return this.presentation;
        } else if(OEAssetSelectionType.mediaCenter) {
            return this.mediaCenterItems;
        }
    }

    hasItem(item)   {
        if(!item || typeof(item) !== 'object' || typeof(item.type) !== 'string' || typeof(item.ids) !== 'object')   return false;
        let searchList = this.getItemListForType(item.type);
        return searchList && searchList.findIndex(searchItem => searchItem.ids.first === item.ids.first && searchItem.ids.second === item.ids.second) >= 0;
    }

    setSelection(selection, props) {
        props = props || this.props;

        let newSelection = [];

        if(selection)  {
            if(Array.isArray(selection))    {
                newSelection = selection.filter(selItem => this.hasItem(selItem)).map(selItem => clone(selItem));
            } else if(this.hasItem(selection))  {
                newSelection.push(clone(selection));
            }
        }

        if(!props.allowsMultipleSelection && newSelection.length > 1)  newSelection = [newSelection[0]];

        if(OEToolbox.shallowEqual(newSelection, this.selection, true))  return;
        this.selection = newSelection;
        this.onSelectionChanged();
    }

    addToSelection(items)   {
        let itemsToAdd = [];

        if(items)  {
            if(Array.isArray(items))    {
                itemsToAdd = items.filter(selItem => this.hasItem(selItem) && !this.isSelected(selItem)).map(selItem => clone(selItem));
            } else if(this.hasItem(items) && !this.isSelected(items))  {
                itemsToAdd.push(clone(items));
            }
        }

        if(!itemsToAdd.length)    return;

        let newSelection = this.props.allowsMultipleSelection ? this.selection.concat(itemsToAdd) : [itemsToAdd[0]];

        if(OEToolbox.shallowEqual(newSelection, this.selection, true))  return;
        this.selection = newSelection;
        this.onSelectionChanged();
    }

    removeFromSelection(items)  {
        let itemsToRemove = [];

        if(items)  {
            if(Array.isArray(items))    {
                itemsToRemove = items;
            } else if(typeof(items) === 'object')  {
                itemsToRemove.push(items);
            }
        }

        if(!itemsToRemove.length)    return;

        let newSelection = this.selection.filter((selItem) => itemsToRemove.findIndex(item => selItem.type === item.type && selItem.ids.first === item.ids.first && selItem.ids.second === item.ids.second) < 0);
        if(OEToolbox.shallowEqual(newSelection, this.selection, true))  return;
        this.selection = newSelection;
        this.onSelectionChanged();
    }

    isSelected(item) {
        return this.selection.findIndex(selItem => selItem.type === item.type && selItem.ids.first === item.ids.first && selItem.ids.second === item.ids.second) >= 0;
    }

    outputSelection()   {
        let selection = this.getSortedSelection();
        return selection.length === 0 ? null : (selection.length === 1 ? selection[0] : selection);
    }

    onSelectionChanged()    {
        //console.log('onSelectionChanged - ' + this.selection.length);
        this.updateSelection();
    }

    updateAnimations(props)  {
        if(!this.oe.isReady())  return;

        props = props || this.props;
        if(!this.hasAnimationType(props))   {
            let animations = this.animations;
            this.animations = [];
            this.setState({animations: clone(this.animations)});
            this.removeFromSelection(animations);
            return;
        }

        let animationDataList = this.oe.sharedInterface.getUIControllerAnimation().getAnimations();

        this.animations = animationDataList.map((anim, index) => {
            let item = {
                type: OEAssetSelectionType.animation,
                ids: {first: index, second: -1},
                thumbImagePath: anim.previewImagePath,
                name: anim.label
            };
            item.selected = this.isSelected(item);
            return item;
        });

        this.selection = this.selection.filter(selItem => this.hasItem(selItem));
        this.setState({animations: clone(this.animations), hasSelection: this.selection.length > 0});
    }

    updatePresentations(props)   {
        if(!this.oe.isReady())  return;

        props = props || this.props;
        if(!this.hasPresentationType(props))   {
            let presentations = this.presentations;
            this.presentations = [];
            this.setState({presentations: clone(this.presentations)});
            this.removeFromSelection(presentations);
            return;
        }

        let presentationDataList = this.oe.sharedInterface.getUIControllerPresentation().getPresentationDataList();

        this.presentations = presentationDataList.map((pres) => {
            let item = {
                type: OEAssetSelectionType.presentation,
                ids: {first: pres.id, second: -1}
            };
            item.selected = this.isSelected(item);
            return item;
        });

        this.selection = this.selection.filter(selItem => this.hasItem(selItem));
        this.setState({presentations: clone(this.presentations), hasSelection: this.selection.length > 0});
    }

    assetTypeFromMediaCenterType(type)  {
        const MCItemType = this.oe.Module.MediaCenterItemType;

        if(type === MCItemType.video)    {
            return OEAssetSelectionType.video;
        } else if(type === MCItemType.image)    {
            return OEAssetSelectionType.image;
        } else if(type === MCItemType.html)    {
            return OEAssetSelectionType.web;
        } else if(type === MCItemType.pdf)    {
            return OEAssetSelectionType.pdf;
        }
    }

    updateMediaCenterItems(props)   {
        if(!this.oe.isReady())  return;

        props = props || this.props;
        if(!this.hasMediaCenterType(props))   {
            let mediaCenterItems = this.mediaCenterItems;
            this.mediaCenterItems = [];
            this.setState({mediaCenterItems: clone(this.mediaCenterItems)});
            this.removeFromSelection(mediaCenterItems);
            return;
        }

        const MCItemType = this.oe.Module.MediaCenterItemType;

        let mediaItemDataList = this.oe.sharedInterface.getUIControllerMediaCenter().getMediaItemDataList();

        mediaItemDataList = mediaItemDataList.filter(item => this.hasType(this.assetTypeFromMediaCenterType(item.type), props));

        this.mediaCenterItems = mediaItemDataList.map((item) => {
            let item_ = {
                type: this.assetTypeFromMediaCenterType(item.type),
                ids: {first: item.id, second: -1},
                thumbImagePath: item.thumbImagePath !== '' ? item.thumbImagePath : (item.previewImagePath !== '' ? item.previewImagePath : (item.type === MCItemType.image ? item.path : null)),
                name: item.label
            };
            item_.selected = this.isSelected(item_);
            return item_;
        });

        this.selection = this.selection.filter(selItem => this.hasItem(selItem));
        this.setState({mediaCenterItems: clone(this.mediaCenterItems), hasSelection: this.selection.length > 0});
    }

    updateSelection()   {
        this.animations.forEach((item) => {
            item.selected = this.isSelected(item);
        });

        this.presentations.forEach((item) => {
            item.selected = this.isSelected(item);
        });

        this.mediaCenterItems.forEach((item) => {
            item.selected = this.isSelected(item);
        });

        this.setState({
            animations: clone(this.animations),
            presentations: clone(this.presentations),
            mediaCenterItems: clone(this.mediaCenterItems),
            hasSelection: this.selection.length > 0
        });
    }

    updateState(released)   {
        if(!this.oe.isReady() || released === true)   {
            this.animations = [];
            this.presentations = [];
            this.mediaCenterItems = [];
            this.selection = [];
            this.setState({
                animations: clone(this.animations),
                presentations: clone(this.presentations),
                mediaCenterItems: clone(this.mediaCenterItems),
                hasSelection: false
            });
            return;
        }

        retardUpdate(this, () => {
            this.updateAnimations();
            this.updatePresentations();
            this.updateMediaCenterItems();
        });
    }

    onPresentationAdded()   {
        this.updatePresentations();
    }

    onPresentationRemoved() {
        this.updatePresentations();
    }

    onMediaCenterItemAdded()   {
        this.updateMediaCenterItems();
    }

    onMediaCenterItemRemoved()   {
        this.updateMediaCenterItems();
    }

    onMediaCenterItemDataChanged()   {
        this.updateMediaCenterItems();
    }

    renderCellFn(indexOffset)    {
        return (item, index) => 
            <OEAssetSelectionCell
                key={indexOffset + index}
                moduleId={this.props.moduleId}
                index={index}
                {...item}
                onClick={this.onCellClick}
            />;
    }

    render()    {
        let animationCells = this.state.animations.map(this.renderCellFn(0));
        let presentationCells = this.state.presentations.map(this.renderCellFn(animationCells.length));
        let mediaCenterCells = this.state.mediaCenterItems.map(this.renderCellFn(presentationCells.length + animationCells.length));

        return(
            <React.Fragment>
                <OEInterfaceAdapter moduleId={this.props.moduleId} receiver={this}/>
                <div className="asset-selection-controller">
                    <div className="container">
                        <CustomScrollbar
                            horizontalTrackVisiblity={CustomScrollbarTrackVisibility.none}
                            verticalTrackVisiblity={CustomScrollbarTrackVisibility.always}
                        >
                            {animationCells}
                            {presentationCells}
                            {mediaCenterCells}
                        </CustomScrollbar>
                    </div>
                    <div className="group-control-separator std-separator-border-color"/>
                    <div className="bottom-bar">
                        <OEButton
                            className="transparent-btn ok-btn"
                            onPressed={this.onOkBtnPressed}
                            disabled={!this.state.hasSelection}
                        >
                            <OEIcon code={OEIconCodes.assetSelection.ok}/>
                        </OEButton>
                    </div>
                </div>
            </React.Fragment>
        );
    }

    onCellClick(type, ids, index, e)   {
        let item = {type: type, ids: ids};

        if(!this.props.allowsMultipleSelection || (!e.ctrlKey && !e.shiftKey) || (!e.ctrlKey && e.shiftKey && this.selection.length === 0)) {
            this.setSelection(item);
            this.pivotItem = item;
        } else if(e.ctrlKey)   {
            if(this.isSelected(item))   {
                this.removeFromSelection(item);
            } else {
                this.addToSelection(item);
                this.pivotItem = item;
            }
        } else if(e.shiftKey)   {
            let sortedSelection = this.getSortedSelection();
            if(!sortedSelection.length) return;

            let pivotIndex = this.getIndex(this.pivotItem);
            if(pivotIndex < 0 || !this.isSelected(this.pivotItem))  {
                pivotIndex = sortedSelection[0].index;
                this.pivotItem = this.getItem(pivotIndex);
            }

            let minMax = {
                min: pivotIndex,
                max: index
            };

            if(minMax.min > minMax.max) {
                let i = minMax.min;
                minMax.min = minMax.max; minMax.max = i;
            }

            let selection = [];
            for(let i = minMax.min; i <= minMax.max; ++i)   {
                let item = this.getItem(i);
                if(!item)   continue;
                selection.push(item);
            }

            this.setSelection(selection);
        }
    }

    onOkBtnPressed()   {
        if(this.props.onOkBtnPressed)   this.props.onOkBtnPressed(this.outputSelection());
    }
}

OEAssetSelectionController.defaultProps = {
    moduleId:                   '',
    types:                      [OEAssetSelectionType.animation, OEAssetSelectionType.presentation],
    allowsMultipleSelection:    false
};

OEAssetSelectionController.propTypes = {
    moduleId:                   PropTypes.string,
    types:                      PropTypes.arrayOf(PropTypes.oneOf(OEAssetSelectionType.all)),
    allowsMultipleSelection:    PropTypes.bool,
    onOkBtnPressed:             PropTypes.func
};

//
export const OEAssetSelectionPopover = OEPopover.coat(OEAssetSelectionController, {}, {
    placement:                  'right',
    buttonClassName:            'transparent-btn',
    titleId:                    'asset_selection_view',
    controllerClassName:        ''
}, {
    controllerClassName:        PropTypes.string
});

//
export default withIsOpenState(OEAssetSelectionPopover);